import * as React from 'react';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import CircularProgress from '@mui/material/CircularProgress';
import Button from '@mui/material/Button';

const styles = require('./styles.pcss');

type Props = {
  events: any[];
  isLoadingMore: boolean;
  hasMore: boolean;
  onLoadMore: () => void;
  onDetailsClick: (event: any) => void;
};

const formatAuditDate = (value?: string) => {
  if (!value) return '—';
  return new Date(value).toLocaleString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    timeZone: 'UTC',
    timeZoneName: 'short',
  });
};

function AuditEventsTable({ events, isLoadingMore, hasMore, onLoadMore, onDetailsClick }: Props) {
  const sentinelRef = React.useRef<HTMLDivElement>(null);

  React.useEffect(() => {
    const sentinel = sentinelRef.current;
    if (!sentinel || !hasMore || isLoadingMore) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) {
          onLoadMore();
        }
      },
      { threshold: 0.1 },
    );

    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [hasMore, isLoadingMore, onLoadMore]);

  return (
    <>
      <Table>
        <TableHead>
          <TableRow>
            <TableCell>Identifier</TableCell>
            <TableCell>Namespace</TableCell>
            <TableCell>Name</TableCell>
            <TableCell>Organization ID</TableCell>
            <TableCell>Performed By</TableCell>
            <TableCell>Created At</TableCell>
            <TableCell />
          </TableRow>
        </TableHead>
        <TableBody>
          {events.map((event: any, index: number) => (
            <TableRow key={event.id || index}>
              <TableCell>{event.identifier ?? '—'}</TableCell>
              <TableCell>{event.namespace ?? '—'}</TableCell>
              <TableCell>{event.name ?? '—'}</TableCell>
              <TableCell>{event.organizationId ?? event.labels?.organizationId ?? '—'}</TableCell>
              <TableCell>
                {event.performedBy?.name && event.performedBy?.email
                  ? `${event.performedBy.name} - ${event.performedBy.email}`
                  : event.performedBy?.name || '—'}
              </TableCell>
              <TableCell>{formatAuditDate(event.createdAt)}</TableCell>
              <TableCell padding="checkbox">
                <Button size="small" onClick={() => onDetailsClick(event)}>
                  Details
                </Button>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>

      {hasMore && (
        <div ref={sentinelRef} className={styles.sentinel}>
          {isLoadingMore && <CircularProgress size={24} />}
        </div>
      )}

      {!hasMore && events.length > 0 && (
        <p className={styles.endOfList}>All events loaded.</p>
      )}
    </>
  );
}

export default AuditEventsTable;
